import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Btn, Alert, Tabs, StatusBadge, StandardModal, colors, inputStyle, selectStyle, textareaStyle } from "../design-system";


const API = "/api";
const token = () => localStorage.getItem("token");
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

const EV_COLORS = { outstanding: "#94a3b8", submitted: "#f59e0b", accepted: "#22c55e", rejected: "#ef4444" };

function EvidenceCard({ item, onUpload, onReview, canReview }) {
  const fileRef = useRef();
  const color   = EV_COLORS[item.status] || "#94a3b8";
  return (
    <div style={{ padding: 14, border: `1px solid ${color}44`, borderRadius: 8, background: color + "0a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, marginBottom: 2 }}>{item.category}</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{item.category_label}</div>
          {item.file_name && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{item.file_name}</div>}
          {item.notes && <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 4, fontStyle: "italic" }}>{item.notes}</div>}
          {item.reviewer_note && (
            <div style={{ fontSize: 12, marginTop: 4, padding: "4px 8px", background: color + "22", borderRadius: 4, color }}>
              Reviewer: {item.reviewer_note}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          <span style={{ background: color + "22", color, border: `1px solid ${color}55`, borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>
            {item.status}
          </span>
          {item.status === "outstanding" || item.status === "rejected" ? (
            <>
              <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => onUpload(item.category, e.target.files[0])} />
              <Btn size="sm" variant="outline" onClick={() => fileRef.current.click()}>Upload</Btn>
            </>
          ) : null}
          {canReview && item.status === "submitted" && (
            <div style={{ display: "flex", gap: 4 }}>
              <Btn size="sm" variant="success" onClick={() => onReview(item.id, "accepted")}>Accept</Btn>
              <Btn size="sm" variant="danger"  onClick={() => onReview(item.id, "rejected")}>Reject</Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CPDYearSummary({ membership_id }) {
  const [cpd, setCpd]   = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ activity_date: "", activity_type: "Formal Qualification", description: "", hours: "", category: "sector" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/memberships/applications/${membership_id}/cpd?year=${year}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => d && setCpd(d));
  }, [membership_id, year]);

  useEffect(() => { load(); }, [load]);

  async function saveCPD(e) {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/memberships/applications/${membership_id}/cpd`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ ...form, hours: parseFloat(form.hours) }),
    });
    if (res.ok) { setAdding(false); setForm({ activity_date: "", activity_type: "Formal Qualification", description: "", hours: "", category: "sector" }); load(); }
    setSaving(false);
  }

  if (!cpd) return <div style={{ color: colors.textMuted, fontSize: 14 }}>Loading CPD…</div>;

  const pct = Math.min(cpd.percentage, 100);
  const barColor = pct >= 100 ? "#22c55e" : pct >= 80 ? "#f59e0b" : "#ef4444";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ padding: "4px 8px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13 }}>
            {[0,1,2].map(i => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
          </select>
          <div>
            <span style={{ fontWeight: 700, fontSize: 18 }}>{cpd.logged_hours.toFixed(1)}</span>
            <span style={{ color: colors.textMuted, fontSize: 14 }}>/{cpd.target_hours}h</span>
          </div>
        </div>
        <Btn size="sm" variant="outline" onClick={() => setAdding(true)}>+ Add CPD</Btn>
      </div>

      <div style={{ height: 8, background: colors.border, borderRadius: 999, marginBottom: 16, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 999, transition: "width 0.4s" }} />
      </div>

      {cpd.records.filter(r => r.year === year).length === 0 ? (
        <p style={{ color: colors.textMuted, fontSize: 14, textAlign: "center", padding: 24 }}>No CPD records for {year}.</p>
      ) : cpd.records.filter(r => r.year === year).map(r => (
        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.description}</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>{r.activity_type} · {r.category} · {new Date(r.activity_date).toLocaleDateString("en-GB")}</div>
          </div>
          <span style={{ fontWeight: 700, fontSize: 14, color: colors.primary }}>{r.hours}h</span>
        </div>
      ))}

      {adding && (
        <StandardModal title="Add CPD Activity" onClose={() => setAdding(false)} width={480}
          footer={<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setAdding(false)}>Cancel</Btn>
            <Btn variant="primary" type="submit" form="cpd-form" disabled={saving}>{saving ? "Saving…" : "Save"}</Btn>
          </div>}
        >
          <form id="cpd-form" onSubmit={saveCPD}>
            {[
              ["Date", "date", "activity_date"],
              ["Description", "text", "description"],
              ["Hours", "number", "hours"],
            ].map(([label, type, field]) => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>{label} *</label>
                <input required type={type} step={type === "number" ? "0.5" : undefined}
                  value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: "border-box" }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Activity Type</label>
              <select value={form.activity_type} onChange={e => setForm(f => ({ ...f, activity_type: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14 }}>
                {["Formal Qualification","Accredited Course","Conference","Workplace Practice","Self-Directed Study","Structured Reflection","Delivery of CPD to Others","Membership of Professional Body"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14 }}>
                <option value="sector">Sector / Occupational</option>
                <option value="professional">Professional Development</option>
                <option value="general">General</option>
              </select>
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}

const TABS = [
  { key: "details",   label: "Application Details" },
  { key: "evidence",  label: "Evidence" },
  { key: "cpd",       label: "CPD" },
  { key: "decisions", label: "Decision Log" },
];

export default function MembershipApplication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp]           = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [tab, setTab]           = useState("details");
  const [loading, setLoading]   = useState(true);
  const [msg, setMsg]           = useState(null);
  const [msgType, setMsgType]   = useState("success");
  const [submitModal, setSubmitModal] = useState(false);
  const [editing, setEditing]   = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    try {
      const [aRes, eRes, dRes] = await Promise.all([
        fetch(`${API}/memberships/applications/${id}`, { headers: authHeaders() }),
        fetch(`${API}/memberships/applications/${id}/evidence`, { headers: authHeaders() }),
        fetch(`${API}/memberships/applications/${id}/decision-log`, { headers: authHeaders() }),
      ]);
      if (aRes.ok) { const d = await aRes.json(); setApp(d); setEditForm(d); }
      if (eRes.ok) setEvidence(await eRes.json());
      if (dRes.ok) setDecisions(await dRes.json());
    } catch (e) {
      flash("Failed to load application", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleUpload(category, file) {
    const fd = new FormData();
    fd.append("category", category);
    fd.append("file", file);
    const res = await fetch(`${API}/memberships/applications/${id}/evidence`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token()}` },
      body: fd,
    });
    if (res.ok) { flash("Evidence uploaded"); load(); }
    else flash("Upload failed", "error");
  }

  async function handleReview(evId, status) {
    const note = status === "rejected" ? prompt("Rejection reason:") : null;
    const res = await fetch(`${API}/memberships/evidence/${evId}/review`, {
      method: "PATCH", headers: authHeaders(),
      body: JSON.stringify({ status, reviewer_note: note }),
    });
    if (res.ok) { flash(`Evidence ${status}`); load(); }
    else flash("Review failed", "error");
  }

  async function handleSubmit() {
    const res = await fetch(`${API}/memberships/applications/${id}/submit`, { method: "POST", headers: authHeaders() });
    if (res.ok) { flash("Application submitted for review"); setSubmitModal(false); load(); }
    else flash("Failed to submit", "error");
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/memberships/applications/${id}`, {
      method: "PATCH", headers: authHeaders(), body: JSON.stringify(editForm),
    });
    if (res.ok) { flash("Application updated"); setEditing(false); load(); }
    else flash("Failed to save", "error");
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 32, color: colors.textMuted }}>Loading…</div>;
  if (!app)    return <div style={{ padding: 32, color: colors.danger }}>Application not found.</div>;

  const canEdit = app.status === "draft";
  const canSubmit = app.status === "draft";
  const isAdmin = JSON.parse(localStorage.getItem("user") || "{}").role === "super_admin";
  const evAccepted  = evidence.filter(e => e.status === "accepted").length;
  const evTotal     = evidence.length;

  const DECISION_TYPE_LABELS = {
    submit: "Submitted", start_review: "Review Started", approve: "Approved",
    conditional_approve: "Conditionally Approved", reject: "Rejected",
    suspend: "Suspended", revoke: "Revoked", reinstate: "Reinstated",
    condition_resolved: "Condition Resolved", note: "Note", renew: "Renewed",
  };
  const DECISION_COLORS = {
    approve: "#22c55e", conditional_approve: "#f59e0b", reject: "#ef4444",
    suspend: "#f97316", revoke: "#dc2626", reinstate: "#22c55e",
    condition_resolved: "#22c55e", note: "#6366f1", submit: "#0ea5e9", start_review: "#0ea5e9", renew: "#22c55e",
  };

  return (
    <div style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Btn variant="ghost" size="sm" onClick={() => navigate("/membership")}>← Back</Btn>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{app.applicant_name}</h1>
            <StatusBadge status={app.status} />
          </div>
          {app.membership_number && (
            <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700, color: colors.primary, marginLeft: 64 }}>
              {app.membership_number} · {app.post_nominal}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {canEdit && (
            <>
              <Btn variant="outline" size="sm" onClick={() => setEditing(true)}>Edit</Btn>
              <Btn variant="primary" size="sm" onClick={() => setSubmitModal(true)}>Submit Application</Btn>
            </>
          )}
          {isAdmin && !canEdit && (
            <Btn variant="primary" size="sm" onClick={() => navigate(`/membership/applications/${id}/decision`)}>
              Decision Panel
            </Btn>
          )}
        </div>
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <Tabs tabs={TABS} activeTab={tab} onChange={setTab} style={{ marginBottom: 24 }} />

      {/* ---- DETAILS ---- */}
      {tab === "details" && (
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
          <Card padding={24}>
            <h3 style={{ fontWeight: 600, marginBottom: 20, fontSize: 16 }}>Applicant Details</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[
                ["Name",         app.applicant_name],
                ["Email",        app.applicant_email],
                ["Phone",        app.applicant_phone || "—"],
                ["Job Title",    app.job_title || "—"],
                ["Organisation", app.organisation || "—"],
                ["Grade",        `${app.grade_name} (${app.post_nominal})`],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{val}</div>
                </div>
              ))}
            </div>
            {app.personal_statement && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Personal Statement</div>
                <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: colors.textSecondary }}>{app.personal_statement}</p>
              </div>
            )}
          </Card>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card padding={20}>
              <h3 style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>Membership</h3>
              {[
                ["Status", <StatusBadge status={app.status} />],
                ["Number",    app.membership_number || "Not yet assigned"],
                ["Valid From",  app.valid_from ? new Date(app.valid_from).toLocaleDateString("en-GB") : "—"],
                ["Valid Until", app.valid_until ? new Date(app.valid_until).toLocaleDateString("en-GB") : "—"],
                ["Billing",   `${app.billing_years} year(s)`],
                ["Fee",       app.final_fee ? `£${app.final_fee.toFixed(2)}` : "—"],
                ["CPD Target", `${app.cpd_target}h/year`],
              ].map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${colors.border}`, fontSize: 13 }}>
                  <span style={{ color: colors.textMuted }}>{label}</span>
                  <span style={{ fontWeight: 500 }}>{val}</span>
                </div>
              ))}
            </Card>

            <Card padding={20}>
              <h3 style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>Evidence Progress</h3>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: evAccepted === evTotal ? colors.success : colors.textSecondary }}>
                  {evAccepted}/{evTotal}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted }}>items accepted</div>
              </div>
            </Card>

            {app.conditions && app.conditions.length > 0 && (
              <Card padding={20} accent={colors.warning}>
                <h3 style={{ fontWeight: 600, marginBottom: 12, fontSize: 14, color: colors.warning }}>Outstanding Conditions</h3>
                {app.conditions.map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: `1px solid ${colors.border}` }}>
                    <span style={{ color: colors.warning }}>○</span>
                    <span style={{ fontSize: 13 }}>{c}</span>
                  </div>
                ))}
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ---- EVIDENCE ---- */}
      {tab === "evidence" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {evidence.map(item => (
            <EvidenceCard key={item.id} item={item} onUpload={handleUpload} onReview={handleReview} canReview={isAdmin} />
          ))}
        </div>
      )}

      {/* ---- CPD ---- */}
      {tab === "cpd" && (
        <Card padding={24}>
          <CPDYearSummary membership_id={id} />
        </Card>
      )}

      {/* ---- DECISION LOG ---- */}
      {tab === "decisions" && (
        <Card padding={24}>
          {decisions.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: "center", padding: 32 }}>No decisions recorded yet.</p>
          ) : (
            <div style={{ position: "relative" }}>
              {decisions.map((d, i) => {
                const color = DECISION_COLORS[d.decision_type] || colors.textMuted;
                return (
                  <div key={d.id} style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: color + "22", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      {i < decisions.length - 1 && <div style={{ width: 2, flex: 1, background: colors.border, marginTop: 4 }} />}
                    </div>
                    <div style={{ flex: 1, paddingBottom: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color }}>{DECISION_TYPE_LABELS[d.decision_type] || d.decision_type}</span>
                        <span style={{ fontSize: 12, color: colors.textMuted }}>{new Date(d.decided_at).toLocaleString("en-GB")}</span>
                      </div>
                      {d.previous_status && d.new_status && (
                        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>
                          <StatusBadge status={d.previous_status} size="sm" /> → <StatusBadge status={d.new_status} size="sm" />
                        </div>
                      )}
                      <p style={{ fontSize: 13, margin: "6px 0", color: colors.textSecondary }}>{d.rationale}</p>
                      {d.conditions?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                          {d.conditions.map((c, ci) => (
                            <span key={ci} style={{ fontSize: 11, background: colors.background, border: `1px solid ${colors.border}`, borderRadius: 4, padding: "2px 6px", color: colors.warning }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>by {d.decided_by}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Submit confirmation modal */}
      {submitModal && (
        <StandardModal
          title="Submit Application"
          onClose={() => setSubmitModal(false)}
          width={440}
          footer={<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setSubmitModal(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSubmit}>Confirm Submission</Btn>
          </div>}
        >
          <p style={{ margin: 0 }}>Submit this application to ICSC for review?</p>
          <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 8 }}>
            Once submitted you cannot edit the application. Make sure all evidence has been uploaded before submitting.
          </p>
          <div style={{ marginTop: 12, fontSize: 13 }}>
            Evidence ready: <strong>{evAccepted + evidence.filter(e => e.status === "submitted").length}/{evTotal}</strong> items uploaded
          </div>
        </StandardModal>
      )}

      {/* Edit modal */}
      {editing && (
        <StandardModal title="Edit Application" onClose={() => setEditing(false)} width={560}
          footer={<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn variant="primary" type="submit" form="edit-app-form" disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Btn>
          </div>}
        >
          <form id="edit-app-form" onSubmit={handleSaveEdit}>
            {[["Full Name","applicant_name"],["Email","applicant_email"],["Phone","applicant_phone"],["Job Title","job_title"],["Organisation","organisation"]].map(([label, field]) => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
                <input value={editForm[field] || ""} onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: "border-box" }} />
              </div>
            ))}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Personal Statement</label>
              <textarea rows={4} value={editForm.personal_statement || ""} onChange={e => setEditForm(f => ({ ...f, personal_statement: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, resize: "vertical", boxSizing: "border-box" }} />
            </div>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
